'use client'

import React, { useState, useEffect } from 'react'
import { Strategy } from '@/types'
import { 
  Calendar, 
  User, 
  Clock, 
  TrendingUp, 
  Globe, 
  BarChart3,
  Edit3
} from 'lucide-react'
import { getUserProfile } from '../lib/api'

interface StrategyBasicInfoProps {
  strategy?: Strategy
  onEditClick?: () => void
}

const StrategyBasicInfo: React.FC<StrategyBasicInfoProps> = ({ strategy, onEditClick }) => {
  const [creatorName, setCreatorName] = useState<string>('')
  const [loadingCreator, setLoadingCreator] = useState(false)

  useEffect(() => {
    if (strategy?.created_by) {
      fetchCreatorName(strategy.created_by)
    }
  }, [strategy?.created_by])

  const fetchCreatorName = async (userId: string) => {
    try {
      setLoadingCreator(true)
      const userData = await getUserProfile(userId)
      setCreatorName(userData.display_name)
    } catch (error) {
      console.error('Error fetching creator name:', error)
      setCreatorName(`user#${userId.substring(0, 3)}`)
    } finally {
      setLoadingCreator(false)
    }
  }

  if (!strategy) {
    return (
      <div className="text-center py-8">
        <p className="text-white/50">加载中...</p>
      </div>
    )
  }

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      trend: '趋势跟踪',
      grid: '网格交易',
      mean_reversion: '均值回归',
      breakout: '突破策略',
      scalping: '高频交易',
      other: '其他'
    }
    return typeMap[type] || type
  }

  const getMarketLabel = (market: string) => {
    const marketMap: Record<string, string> = {
      crypto: '加密货币',
      forex: '外汇',
      stocks: '股票',
      commodities: '商品',
      other: '其他'
    }
    return marketMap[market] || market
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: '草稿',
      test: '验证中',
      live: '实盘中'
    }
    return statusMap[status] || status
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'test':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gradient">策略基本信息</h2>
        <button 
          className="btn-secondary flex items-center whitespace-nowrap"
          onClick={onEditClick}
        >
          <Edit3 className="w-4 h-4 mr-2" />
          编辑
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 基本信息 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">基本信息</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">策略名称</span>
              <span className="text-sm text-white font-medium">{strategy.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">策略类型</span>
              <div className="flex items-center">
                <TrendingUp className="w-4 h-4 mr-1 text-violet-400" />
                <span className="text-sm text-white">{getTypeLabel(strategy.type)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">交易市场</span>
              <div className="flex items-center">
                <Globe className="w-4 h-4 mr-1 text-violet-400" />
                <span className="text-sm text-white">{getMarketLabel(strategy.market)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">交易对</span>
              <span className="text-sm text-white">{strategy.market_symbol}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">时间周期</span>
              <div className="flex items-center">
                <BarChart3 className="w-4 h-4 mr-1 text-violet-400" />
                <span className="text-sm text-white">{strategy.timeframe}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">状态</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(strategy.status)}`}>
                {getStatusLabel(strategy.status)}
              </span>
            </div>
          </div>
        </div>

        {/* 时间信息 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">时间信息</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">创建时间</span>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1 text-violet-400" />
                <span className="text-sm text-white">
                  {new Date(strategy.created_at).toLocaleString('zh-CN')}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">最后更新</span>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1 text-violet-400" />
                <span className="text-sm text-white">
                  {new Date(strategy.updated_at).toLocaleString('zh-CN')}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">创建者</span>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1 text-violet-400" />
                <span className="text-sm text-white">
                  {loadingCreator ? '加载中...' : (creatorName || '未知')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 策略描述 */}
      {strategy.description && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">策略描述</h3>
          <p className="text-white/80 leading-relaxed">{strategy.description}</p>
        </div>
      )}
    </div>
  )
}

export default StrategyBasicInfo 